/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import currency from "currency.js";
import moment from "moment";
import { useMemo } from "react";

import {
  CollateralAssetResponse,
  InstrumentType,
  OrderTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { getAssetShortName, getCollateralPrecision } from "../../../utils/asset/assets";
import { getTimeAgo, nanosToSeconds } from "../../../utils/date";
import { formatSizePrecision } from "../../../utils/format";
import { Pill } from "../../../pages/PortfolioPage/mobile/style";
import TickerLogo from "../TickerLogo";
import {
  FillNotificationBody,
  FillNotificationTitle,
  IconWrapper,
  NotificationWrapper,
  PillContent,
  ReadIndicator,
  SettlementNotificationBody,
  SettlementNotificationTitle,
  SettlementType,
  Timestamp,
  Title,
} from "./style";
import {
  activityMoment,
  formatPerpFillInstrument,
  getFillAsset,
  normalizeFillNumeric,
} from "./helpers";
import { IMarketNotificationProps } from "./types";

export function FillNotification({
  notification,
  t,
  translateAll,
  getMarketPrecision,
}: IMarketNotificationProps) {
  const { metadata } = notification;
  const fillAsset = getFillAsset(metadata);
  const fillInstrumentLabel = formatPerpFillInstrument(metadata);

  const isCollateralConversion = useMemo(
    () =>
      (Object.values(CollateralAssetResponse) as string[]).includes(
        fillAsset || ""
      ),
    [fillAsset]
  );

  const fillTitle = useMemo(() => {
    if (metadata?.option_type) {
      return (
        <FillNotificationTitle>
          <span>{metadata?.option_type}&nbsp;-&nbsp;</span>
          <span>
            {metadata?.expiry
              ? moment
                  .unix(nanosToSeconds(metadata?.expiry))
                  .format("DD MMM YY")
              : ""}
            &nbsp;-&nbsp;
          </span>
          <span>{metadata?.strike}</span>
        </FillNotificationTitle>
      );
    }

    if (isCollateralConversion) {
      // Spot
      return (
        <FillNotificationTitle>
          <span>
            {getAssetShortName(fillAsset)} {t("spot")}
          </span>
        </FillNotificationTitle>
      );
    }

    return (
      <FillNotificationTitle>
        <span>{fillInstrumentLabel}</span>
      </FillNotificationTitle>
    );
  }, [fillAsset, fillInstrumentLabel, isCollateralConversion, metadata, t]);

  const fillPill = useMemo(() => {
    if (isCollateralConversion) {
      return undefined;
    }

    if (metadata?.order_type === OrderTypeResponse.Market) {
      return t("market_order_filled");
    }
    return metadata?.side === SideResponse.Buy
      ? t("bid_filled")
      : t("ask_filled");
  }, [isCollateralConversion, metadata?.order_type, metadata?.side, t]);

  let { amount_precision, price_precision } = getMarketPrecision(
    fillAsset,
    metadata?.option_type ? InstrumentType.Option : InstrumentType.Perpetual
  );

  if (isCollateralConversion) {
    price_precision = 2;
    amount_precision =
      getCollateralPrecision(fillAsset as CollateralAssetResponse) || 2;
  }

  // Defensive scaler: legacy fills cached in Redis were stored as raw
  // 18-decimal fixed-point integers (e.g. price ~= 2.1e21 instead of 2112.9).
  // Any value beyond 1e10 is impossibly large for a real price/size, so we
  // scale it down by 1e18 to recover the display value.
  const displayAmount = normalizeFillNumeric(metadata?.amount);
  const displayPrice = normalizeFillNumeric(metadata?.price);

  // Show the USD value alongside the size. Spot sizes are small enough that the raw number is
  // hard to read at a glance -- a 10 USDC buy of BTC renders as 0.000129, so you have to count
  // leading zeros to judge the trade. Derived here rather than added to the notification
  // metadata so it applies to already-stored rows too, and so there is one less field to keep
  // in sync between the two backend writers.
  const notionalUsd = displayAmount * displayPrice;
  const showNotional = Number.isFinite(notionalUsd) && notionalUsd > 0;

  return (
    <NotificationWrapper isRead={notification.is_read}>
      <IconWrapper>
        <TickerLogo asset={fillAsset} instrumentName={metadata?.instrument} size={32} />
        {!notification.is_read && <ReadIndicator />}
      </IconWrapper>
      <div>
        <Title>
          {fillTitle}
          {fillPill && (
            <Pill
              $isPositive={metadata?.side === SideResponse.Buy}
              $borderRadius={"4px"}
            >
              <PillContent>{fillPill}</PillContent>
            </Pill>
          )}
        </Title>
        <FillNotificationBody>
          <span>
            {formatSizePrecision(displayAmount, amount_precision)}
          </span>
          {showNotional && (
            <span style={{ color: TEXT_COLORS.three }}>
              {`(${currency(notionalUsd, { precision: 2 }).format()})`}
            </span>
          )}
          <span style={{ color: TEXT_COLORS.three }}>
            {t("asset_at", {
              asset: getAssetShortName(fillAsset) || fillAsset || "",
            })}
          </span>
          <span>
            {currency(displayPrice, {
              precision: price_precision,
            }).format()}
          </span>
        </FillNotificationBody>
        <Timestamp>
          {getTimeAgo(activityMoment(notification), translateAll)}
        </Timestamp>
      </div>
    </NotificationWrapper>
  );
}

export function SettlementNotification({
  notification,
  t,
  translateAll,
  getMarketPrecision,
}: IMarketNotificationProps) {
  const { metadata } = notification;

  const { price_precision, amount_precision } = getMarketPrecision(
    metadata?.asset,
    InstrumentType.Option
  );

  const pnlCurrency = currency(
    metadata?.side === SideResponse.Buy
      ? Number(metadata?.payout || 0)
      : Number(metadata?.payout || 0) + Number(metadata?.entry_price || 0),
    { precision: price_precision, pattern: "+!#" }
  );

  let inTheMoney = false;
  // Long, positive payout = itm, else otm
  // Short, negative payout = itm, else otm
  if (
    (metadata?.side === SideResponse.Buy && pnlCurrency.value > 0) ||
    (metadata?.side === SideResponse.Sell && pnlCurrency.value < 0)
  ) {
    inTheMoney = true;
  }

  const fillTitle = useMemo(
    () => (
      <SettlementNotificationTitle>
        <span>
          <span>{metadata?.option_type}&nbsp;-&nbsp;</span>
          <span>
            {metadata?.expiry
              ? moment
                  .unix(nanosToSeconds(metadata?.expiry))
                  .format("DD MMM YY")
              : ""}
            &nbsp;-&nbsp;
          </span>
          <span>{metadata?.strike}</span>
        </span>
        <SettlementType side={metadata?.side}>
          {metadata?.side === SideResponse.Sell
            ? t("short_settlement")
            : t("long_settlement")}
        </SettlementType>
      </SettlementNotificationTitle>
    ),
    [metadata, t]
  );

  return (
    <NotificationWrapper isRead={notification.is_read}>
      <IconWrapper>
        <TickerLogo asset={notification.metadata?.asset} size={32} />
        {!notification.is_read && <ReadIndicator />}
      </IconWrapper>
      <div>
        <Title>{fillTitle}</Title>
        <SettlementNotificationBody>
          <span>
            <strong>
              {metadata?.amount
                ? Math.abs(Number(metadata?.amount)).toFixed(amount_precision)
                : "0"}{" "}
            </strong>
            {metadata?.side === SideResponse.Buy
              ? t("contracts_bought_desc")
              : t("contracts_sold_desc")}{" "}
            <strong>
              {inTheMoney ? t("in_the_money") : t("out_the_money")}{" "}
            </strong>
            {pnlCurrency.value >= 0
              ? t("realized_profit_desc")
              : t("realized_loss_desc")}{" "}
            <span
              style={{
                color:
                  pnlCurrency.value >= 0
                    ? COLORS.positive.one
                    : COLORS.negative.one,
              }}
            >
              {pnlCurrency.format()}
            </span>
          </span>
        </SettlementNotificationBody>
        <Timestamp>
          {getTimeAgo(activityMoment(notification), translateAll)}
        </Timestamp>
      </div>
    </NotificationWrapper>
  );
}
